import { Box, Collapse } from '@ltvco/refresh-lib/theme';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import { useState } from 'react';

import {
  AccordionCard,
  AccordionCardButton,
  AccordionDetailList,
  Chevron,
  CompactReportFieldRow,
  ContactRecordItem,
  ContactRecordList,
  ExpandPanelInner,
  ExpandRowCount,
  ExpandRowLabel,
  ExpandRowMain,
  ExpandRowPreview,
  FieldList,
  OwnerAvatar,
  OwnerCard,
  OwnerCardContent,
  OwnerCardHeader,
  OwnerCardName,
  PreviewCardSection,
  ReportFieldRow,
  StatusFieldRow,
  OwnerReportBar,
  OwnerReportButton,
  OwnerReportText,
} from '../../components';
import {
  formatPhoneLinkType,
  formatPhoneNumber,
  formatTimezone,
  getActivityConfig,
  getInitials,
  normalizePhoneDigits,
  titleCase,
  type StatusSeverity,
} from '../utils';

import { LinkedSection } from './components';

import type {
  PhoneContactAddress,
  PhoneContactEmail,
  PhoneContactNumber,
  PhoneFraudInfo,
  PhoneLineInfo,
  PhonePotentialOwner,
  PhoneSpamInfo,
} from 'components/DeepSearch/types';

interface ScanItem {
  label: string;
  value: string;
  severity: StatusSeverity;
  info: string;
  infoLabel: string;
}

function buildScanItems(
  fraud: PhoneFraudInfo | null,
  spam: PhoneSpamInfo | null,
  phoneLine: PhoneLineInfo | null
): ScanItem[] {
  const items: ScanItem[] = [];

  if (fraud) {
    items.push({
      label: 'Leaked number',
      value: fraud.isLeaked ? 'Yes' : 'No',
      severity: fraud.isLeaked ? 'error' : 'success',
      info: fraud.isLeaked
        ? 'Appeared in a known public data breach.'
        : 'Not found in any known public data breach.',
      infoLabel: 'What does leaked number mean?',
    });
  }

  if (phoneLine?.userActivity) {
    const { severity } = getActivityConfig(phoneLine.userActivity);
    items.push({
      label: 'User activity',
      value: titleCase(phoneLine.userActivity),
      severity,
      info: 'How often this number is reported or searched by the community.',
      infoLabel: 'What is user activity?',
    });
  }

  const spamCount = spam?.spamReportsCount ?? 0;
  items.push({
    label: 'Spam reports',
    value: spamCount === 0 ? 'None' : String(spamCount),
    severity: spamCount === 0 ? 'success' : spamCount > 3 ? 'error' : 'warning',
    info:
      spamCount === 0
        ? 'No community complaints found for this number.'
        : 'Community complaints filed against this number.',
    infoLabel: 'What are spam reports?',
  });

  if (fraud) {
    items.push({
      label: 'DNC list',
      value: fraud.isDoNotCall ? 'Listed' : 'Not listed',
      severity: fraud.isDoNotCall ? 'error' : 'success',
      info: fraud.isDoNotCall
        ? 'Registered on the national Do Not Call list.'
        : 'Not registered on the national Do Not Call list.',
      infoLabel: 'What is the DNC list?',
    });

    items.push({
      label: 'Recent abuse',
      value: fraud.isRecentAbuse ? 'Yes' : 'No',
      severity: fraud.isRecentAbuse ? 'error' : 'success',
      info: 'Recent reports of fraudulent or abusive activity.',
      infoLabel: 'What does recent abuse mean?',
    });
  }

  return items;
}

export const FraudScanSection = ({
  fraud,
  spam,
  phoneLine,
}: {
  fraud: PhoneFraudInfo | null;
  spam: PhoneSpamInfo | null;
  phoneLine: PhoneLineInfo | null;
}) => {
  const items = buildScanItems(fraud, spam, phoneLine);
  if (items.length === 0) {
    return null;
  }

  return (
    <PreviewCardSection title="Fraud scan">
      <FieldList>
        {items.map((item) => (
          <StatusFieldRow key={item.label} {...item} />
        ))}
      </FieldList>
    </PreviewCardSection>
  );
};

export const LineDetailsSection = ({
  phoneLine,
}: {
  phoneLine: PhoneLineInfo;
}) => {
  const location = [
    phoneLine.city ? titleCase(phoneLine.city) : '',
    phoneLine.state,
  ]
    .filter(Boolean)
    .join(', ');

  const rows: [string, string][] = [
    ['Carrier', phoneLine.carrier],
    ['Line type', phoneLine.lineType],
    ['Prepaid', phoneLine.isPrepaid ? 'Yes' : 'No'],
    ['VoIP', phoneLine.isVoip ? 'Yes' : 'No'],
    ['Valid number', phoneLine.isValid ? 'Yes' : 'No'],
    ['Location', location],
    ['Zip', phoneLine.zip5],
    ['Time zone', formatTimezone(phoneLine.timezone)],
  ].filter(([, v]) => v !== '') as [string, string][];

  if (rows.length === 0) {
    return null;
  }

  return (
    <PreviewCardSection title="Line details">
      <FieldList>
        {rows.map(([label, value]) => (
          <ReportFieldRow key={label} label={label} value={value} />
        ))}
      </FieldList>
    </PreviewCardSection>
  );
};

export const PotentialOwnersSection = ({
  owner,
  onSendMessage,
}: {
  owner: PhonePotentialOwner;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const hasContent = !!owner.primaryAddress || !!owner.primaryEmail;
  const isExpandable = hasContent || !!onSendMessage;
  const toggleOpen = () => setOpen((open) => !open);

  function handleSendMessage() {
    if (!onSendMessage) {
      return;
    }
    const content = owner.bvId
      ? `Run a person report on ${owner.name}, bv_id: ${owner.bvId}`
      : `Run a person report on ${owner.name}`;
    onSendMessage(content, `Search for ${owner.name}`);
  }

  return (
    <PreviewCardSection title="Possible owner(s)">
      <OwnerCard>
        <OwnerCardHeader
          type="button"
          aria-expanded={isExpandable ? open : undefined}
          disabled={!isExpandable}
          onClick={isExpandable ? toggleOpen : undefined}
        >
          <OwnerAvatar>{getInitials(owner.name)}</OwnerAvatar>
          <OwnerCardName>{owner.name}</OwnerCardName>
          {isExpandable && <Chevron open={open} />}
        </OwnerCardHeader>
        {isExpandable && (
          <Collapse in={open}>
            <OwnerCardContent>
              {onSendMessage && (
                <OwnerReportBar type="button" onClick={handleSendMessage}>
                  <OwnerReportText>
                    View a complete report for {owner.name}
                  </OwnerReportText>
                  <OwnerReportButton>
                    <ChevronRightIcon sx={{ fontSize: 20 }} />
                  </OwnerReportButton>
                </OwnerReportBar>
              )}
              {hasContent && (
                <AccordionDetailList>
                  {owner.primaryAddress && (
                    <CompactReportFieldRow
                      label="Primary address"
                      value={owner.primaryAddress}
                    />
                  )}
                  {owner.primaryEmail && (
                    <CompactReportFieldRow
                      label="Primary email"
                      value={owner.primaryEmail}
                    />
                  )}
                </AccordionDetailList>
              )}
            </OwnerCardContent>
          </Collapse>
        )}
      </OwnerCard>
    </PreviewCardSection>
  );
};

const contactIconSx = { fontSize: 18, color: 'text.secondary', flexShrink: 0 };

const AddressLinkedRows = ({
  addresses,
  onSendMessage,
}: {
  addresses: PhoneContactAddress[];
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen((open) => !open);
  const preview = addresses
    .slice(0, 2)
    .map((a) => a.full.split(' ').slice(-2).join(', '))
    .join(' · ');

  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <ExpandRowMain>
          <ExpandRowLabel>Possible addresses</ExpandRowLabel>
          {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
        </ExpandRowMain>
        <ExpandRowCount count={addresses.length} />
        <Chevron open={open} />
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <ContactRecordList>
            {addresses.map((addr, i) => (
              <ContactRecordItem
                key={i}
                icon={<LocationOnIcon sx={contactIconSx} />}
                title={addr.full}
                sub={i === 0 ? 'Primary address' : undefined}
                onClick={
                  onSendMessage
                    ? () => {
                        const msg = `Property report for ${addr.full}`;
                        onSendMessage(msg, msg);
                      }
                    : undefined
                }
              />
            ))}
          </ContactRecordList>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

const EmailLinkedRows = ({
  emails,
  onSendMessage,
}: {
  emails: PhoneContactEmail[];
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen((open) => !open);
  const preview = emails[0]?.address ?? '';

  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <ExpandRowMain>
          <ExpandRowLabel>Possible emails</ExpandRowLabel>
          {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
        </ExpandRowMain>
        <ExpandRowCount count={emails.length} />
        <Chevron open={open} />
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <ContactRecordList>
            {emails.map((email, i) => (
              <ContactRecordItem
                key={email.address}
                icon={<EmailIcon sx={contactIconSx} />}
                title={email.address}
                sub={i === 0 ? 'Primary email' : undefined}
                onClick={
                  onSendMessage
                    ? () => {
                        const msg = `Email report for ${email.address}`;
                        onSendMessage(msg, msg);
                      }
                    : undefined
                }
              />
            ))}
          </ContactRecordList>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

const PhoneLinkedRows = ({
  phones,
  currentNumber,
  onSendMessage,
}: {
  phones: PhoneContactNumber[];
  currentNumber: string;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen((open) => !open);
  const currentDigits = normalizePhoneDigits(currentNumber);
  const otherCount = phones.filter(
    (p) => normalizePhoneDigits(p.number) !== currentDigits
  ).length;
  const preview =
    otherCount > 0
      ? `${otherCount} more mobile number${otherCount !== 1 ? 's' : ''} linked`
      : '';

  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <ExpandRowMain>
          <ExpandRowLabel>Other phone numbers</ExpandRowLabel>
          {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
        </ExpandRowMain>
        <ExpandRowCount count={phones.length} />
        <Chevron open={open} />
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <ContactRecordList>
            {phones.map((phone) => {
              const typeLabel = formatPhoneLinkType(phone.type);
              const isCurrentNumber =
                normalizePhoneDigits(phone.number) === currentDigits;
              const sub = [typeLabel, isCurrentNumber ? 'This number' : '']
                .filter(Boolean)
                .join(' · ');

              const formatted = formatPhoneNumber(phone.number);
              return (
                <ContactRecordItem
                  key={phone.number}
                  icon={<PhoneIcon sx={contactIconSx} />}
                  title={formatted}
                  sub={sub || undefined}
                  onClick={
                    onSendMessage && !isCurrentNumber
                      ? () => {
                          const msg = `Phone report for ${formatted}`;
                          onSendMessage(msg, msg);
                        }
                      : undefined
                  }
                />
              );
            })}
          </ContactRecordList>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

const StaticLinkedRow = ({
  label,
  count,
}: {
  label: string;
  count: number;
}) => (
  <AccordionCard>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 16px',
        minHeight: 44,
      }}
    >
      <ExpandRowMain>
        <ExpandRowLabel>{label}</ExpandRowLabel>
      </ExpandRowMain>
      <ExpandRowCount count={count} />
    </Box>
  </AccordionCard>
);

export const LinkedRecordsSection = ({
  addresses,
  emails,
  phones,
  currentNumber,
  jobs,
  education,
  onSendMessage,
}: {
  addresses: PhoneContactAddress[];
  emails: PhoneContactEmail[];
  phones: PhoneContactNumber[];
  currentNumber: string;
  jobs: string[];
  education: string[];
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const hasAny =
    addresses.length > 0 ||
    emails.length > 0 ||
    phones.length > 0 ||
    jobs.length > 0 ||
    education.length > 0;

  if (!hasAny) {
    return null;
  }

  return (
    <PreviewCardSection title="Linked records">
      <LinkedSection>
        {addresses.length > 0 && (
          <AddressLinkedRows
            addresses={addresses}
            onSendMessage={onSendMessage}
          />
        )}
        {emails.length > 0 && (
          <EmailLinkedRows emails={emails} onSendMessage={onSendMessage} />
        )}
        {phones.length > 0 && (
          <PhoneLinkedRows
            phones={phones}
            currentNumber={currentNumber}
            onSendMessage={onSendMessage}
          />
        )}
        {jobs.length > 0 && (
          <StaticLinkedRow label="Jobs" count={jobs.length} />
        )}
        {education.length > 0 && (
          <StaticLinkedRow label="Education" count={education.length} />
        )}
      </LinkedSection>
    </PreviewCardSection>
  );
};
